import { useRef, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/hooks/useLanguage";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function sendMessage(email: string, message: string): Promise<void> {
  const res = await fetch("/api/contact", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, message }),
  });
  if (!res.ok) throw new Error("send failed");
}

type Step = "email" | "message";
type Status = "idle" | "sending" | "sent" | "error";

export function HeroEmailForm() {
  const { t } = useLanguage();
  const f = t.heroForm;
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [invalid, setInvalid] = useState(false);

  const isEmail = step === "email";
  const sending = status === "sending";
  const value = isEmail ? email : message;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isEmail) {
      if (!EMAIL_RE.test(email.trim())) {
        setInvalid(true);
        return;
      }
      setInvalid(false);
      setStep("message"); // wipes the field (now bound to empty message) and morphs the button
      inputRef.current?.focus();
      return;
    }

    if (!message.trim() || sending) return;
    setStatus("sending");
    try {
      await sendMessage(email.trim(), message.trim());
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 text-sm font-medium text-foreground"
      >
        <Check className="h-4 w-4 text-accent" />
        {f.sent}
      </motion.p>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full max-w-md">
      <div
        className={cn(
          "flex items-center gap-1 rounded-full border bg-background/40 p-1 ps-4 backdrop-blur-sm transition-colors",
          invalid
            ? "border-destructive/70"
            : "border-border/70 focus-within:border-foreground/40"
        )}
      >
        <div className="relative min-w-0 flex-1">
          <input
            ref={inputRef}
            type={isEmail ? "email" : "text"}
            value={value}
            onChange={(e) => {
              if (isEmail) {
                setEmail(e.target.value);
                setInvalid(false);
              } else {
                setMessage(e.target.value);
              }
              if (status === "error") setStatus("idle");
            }}
            aria-label={isEmail ? f.emailPlaceholder : f.messagePlaceholder}
            autoComplete={isEmail ? "email" : "off"}
            disabled={sending}
            className="w-full bg-transparent py-1.5 text-sm text-foreground outline-none"
          />
          {/* Animated placeholder — cross-fades as the field's purpose changes. */}
          {value === "" && (
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={step}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="pointer-events-none absolute inset-y-0 start-0 flex items-center text-sm text-muted-foreground"
              >
                {isEmail ? f.emailPlaceholder : f.messagePlaceholder}
              </motion.span>
            </AnimatePresence>
          )}
        </div>

        <button
          type="submit"
          disabled={sending}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-foreground px-4 py-2",
            "text-sm font-medium text-background transition-opacity duration-200 hover:opacity-90 disabled:opacity-70"
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={isEmail ? "next" : sending ? "sending" : "send"}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.16 }}
              className="inline-flex items-center gap-1.5"
            >
              {isEmail ? (
                <>
                  {f.next}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </>
              ) : sending ? (
                <>
                  {f.sending}
                  <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  {f.send}
                  <Send className="h-4 w-4 rtl:-scale-x-100" />
                </>
              )}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {(invalid || status === "error") && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 ps-4 text-xs text-destructive"
          >
            {invalid ? f.invalid : f.error}
          </motion.p>
        )}
      </AnimatePresence>
    </form>
  );
}
